/**
 * KnowledgeSearch — Global search bar for TopBar.
 * Debounced semantic search across resolutions, exceptions, reference content.
 * Ctrl+K / Cmd+K to focus, Escape to close, arrow keys to navigate.
 */
import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchKnowledge, SearchResult } from '../services/knowledgeService';
import { titleCase } from '../constants';

const TYPE_BADGES: Record<string, { label: string; bg: string; color: string }> = {
  resolution:  { label: 'Resolution',  bg: 'var(--accent-subtle)', color: 'var(--navy-800)' },
  exception:   { label: 'Exception',   bg: '#fff7ed', color: '#c2410c' },
  reference:   { label: 'Reference',   bg: '#f0fdf4', color: '#15803d' },
  learning:    { label: 'Learning',    bg: '#f5f3ff', color: '#6d28d9' },
  diagnostic:  { label: 'Diagnostic',  bg: 'var(--bg-elevated)', color: 'var(--text-muted)' },
};

function getBadge(type: string) {
  return TYPE_BADGES[type] || TYPE_BADGES.diagnostic;
}

function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

function KnowledgeSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searched, setSearched] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const resp = await searchKnowledge(q, { topK: 10 });
      setResults(resp.results || []);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Knowledge search error:', err);
      // Don't clear results on error — keep previous results visible
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navigateToResult = (r: SearchResult) => {
    setIsOpen(false);
    setQuery('');
    if (r.content_type === 'resolution' || r.content_type === 'exception') {
      // For resolutions, source_id is the exception_result id.
      // Navigate to the exception detail page.
      // source_table tells us: "exception_results" -> need exception_id,
      // "exceptions" -> source_id IS the exception id.
      if (r.source_table === 'exceptions') {
        navigate(`/activity-tracker/${r.source_id}`);
      } else {
        // For exception_results, the source_id is the result id, not exception id.
        // Use the exception_type or id from the result — but we don't have exception_id directly.
        // Best approach: navigate and let the page resolve it.
        // The result's id in knowledge_embeddings references source_id in exception_results.
        // We'll navigate to the search result's context — the exception_id is embedded in the data.
        // For now, navigate to activity-tracker with the source_id as a query hint.
        navigate(`/activity-tracker?search_result=${r.source_id}`);
      }
    } else {
      // Reference, learning, diagnostic — expand inline (no detail page)
      setExpandedId(expandedId === r.id ? null : r.id);
      return; // Don't close
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault();
      navigateToResult(results[selectedIndex]);
    }
  };

  // Group results by content_type
  const grouped: Record<string, SearchResult[]> = {};
  for (const r of results) {
    const key = r.content_type || 'other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  }
  const groupOrder = ['resolution', 'exception', 'reference', 'learning', 'diagnostic'];
  const sortedGroups = groupOrder.filter(k => grouped[k]);

  // Flat index for keyboard nav
  let flatIndex = -1;

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      {/* Search input */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: isOpen ? 'var(--bg-card)' : 'var(--bg-surface)',
        border: `1px solid ${isOpen ? 'var(--navy-600)' : 'var(--border-default)'}`,
        borderRadius: 10,
        padding: '6px 14px',
        gap: 6,
        transition: 'all 0.25s ease',
        width: isOpen ? 360 : 220,
        minWidth: 160,
        boxShadow: isOpen ? '0 0 0 3px rgba(30,58,95,0.08)' : 'none',
      }}>
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)', flexShrink: 0 }}>{'\u{1F50D}'}</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleInputKeyDown}
          placeholder="Search... (Ctrl+K)"
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text-primary)',
            fontSize: 13,
            width: '100%',
            padding: '2px 0',
          }}
        />
        {loading && (
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0, animation: 'spin 1s linear infinite' }}>
            {'\u{23F3}'}
          </span>
        )}
        {query && !loading && (
          <span
            onClick={() => { setQuery(''); setResults([]); setSearched(false); }}
            style={{ fontSize: 13, color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 }}
          >
            {'\u{2715}'}
          </span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 6,
          width: 420,
          maxHeight: 480,
          overflowY: 'auto',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 8,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          zIndex: 2000,
        }}>
          {/* No query yet — tips */}
          {!searched && !query && (
            <div style={{ padding: '16px 14px', color: 'var(--text-tertiary)', fontSize: 12, lineHeight: 1.6 }}>
              Search across exceptions, resolutions, and reference library.
              <br />
              Try: "intercompany", "revenue recognition", "duplicate invoice"
            </div>
          )}

          {/* Searching indicator */}
          {loading && query.length >= 2 && (
            <div style={{ padding: '12px 14px', color: 'var(--text-tertiary)', fontSize: 12 }}>
              Searching...
            </div>
          )}

          {/* No results */}
          {searched && !loading && results.length === 0 && (
            <div style={{ padding: '16px 14px', color: 'var(--text-tertiary)', fontSize: 12 }}>
              No results found for "{query}". Try different keywords.
            </div>
          )}

          {/* Grouped results — keep visible while loading to prevent flash */}
          {sortedGroups.map(groupKey => {
            const badge = getBadge(groupKey);
            const items = grouped[groupKey];
            return (
              <div key={groupKey}>
                <div style={{
                  padding: '8px 14px 4px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}>
                  {badge.label}s ({items.length})
                </div>
                {items.map(r => {
                  flatIndex++;
                  const idx = flatIndex;
                  const isSelected = idx === selectedIndex;
                  const isExpanded = expandedId === r.id;
                  return (
                    <div
                      key={r.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => navigateToResult(r)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      style={{
                        padding: '8px 14px',
                        cursor: 'pointer',
                        background: isSelected ? 'var(--bg-hover)' : 'transparent',
                        borderLeft: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
                        transition: 'all 0.1s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        {/* Type badge */}
                        <span style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '1px 6px',
                          borderRadius: 3,
                          background: badge.bg,
                          color: badge.color,
                          flexShrink: 0,
                        }}>
                          {badge.label}
                        </span>
                        {/* Exception type */}
                        {r.exception_type && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {titleCase(r.exception_type)}
                          </span>
                        )}
                        {/* Score */}
                        <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 'auto', flexShrink: 0 }}>
                          {(r.composite_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      {/* Text preview */}
                      <div style={{
                        fontSize: 12,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: isExpanded ? 'block' : '-webkit-box',
                        WebkitLineClamp: isExpanded ? undefined : 2,
                        WebkitBoxOrient: 'vertical' as any,
                      }}>
                        {isExpanded ? r.text : truncate(r.text, 120)}
                      </div>
                      {/* Metadata line */}
                      <div style={{ display: 'flex', gap: 8, marginTop: 3, fontSize: 10, color: 'var(--text-tertiary)' }}>
                        {r.entity_code && <span>{r.entity_code}</span>}
                        {r.department && <span>{r.department}</span>}
                        {r.outcome && (
                          <span style={{ color: r.outcome === 'ACCEPTED' ? 'var(--positive)' : r.outcome === 'REJECTED' ? 'var(--negative)' : 'var(--text-tertiary)' }}>
                            {r.outcome}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Footer */}
          {results.length > 0 && (
            <div style={{
              padding: '6px 14px',
              fontSize: 10,
              color: 'var(--text-tertiary)',
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
              <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
              <span>
                <span style={{ opacity: 0.6 }}>Navigate:</span> {'\u{2191}\u{2193}'} <span style={{ opacity: 0.6 }}>Select:</span> Enter <span style={{ opacity: 0.6 }}>Close:</span> Esc
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(KnowledgeSearch);
